import React from 'react';
import { ArrowLeft, Server, Database, BrainCircuit, Play, Newspaper, Copyright, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../i18n';
import { AppFooter } from '../Common/AppFooter';
import SEO from '../Common/SEO';

interface ApiInfoViewProps {
    onBack: () => void;
}

export const ApiInfoView: React.FC<ApiInfoViewProps> = ({ onBack }) => {
    const { lang } = useLanguage();

    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        // PullToRefresh 내부의 스크롤 컨테이너를 찾아 초기화
        const scrollContainers = document.querySelectorAll('.overflow-y-auto');
        scrollContainers.forEach(container => {
            container.scrollTop = 0;
        });
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <SEO title={t(lang, 'apiAndCopyright')} />
            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-6 py-4 flex items-center gap-4">
                <button onClick={onBack} className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full text-slate-600 active:scale-90 transition-all md:hover:bg-rose-50 md:hover:text-rose-500">
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-xl font-black text-slate-900 tracking-tight uppercase">{t(lang, 'apiAndCopyright')}</h1>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-12 custom-scrollbar">
                <div className="max-w-3xl mx-auto">
                    <div className="space-y-12">
                        <div className="space-y-8">
                            <section>
                                <h4 className="text-slate-900 font-black uppercase tracking-widest text-[11px] mb-3 flex items-center gap-2">
                                    <Database size={14} className="text-slate-400" /> {t(lang, 'apiSportsData')}
                                </h4>
                                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                                    {t(lang, 'apiInfoDesc1')}
                                </p>
                            </section>

                            <section>
                                <h4 className="text-slate-900 font-black uppercase tracking-widest text-[11px] mb-3 flex items-center gap-2">
                                    <Play size={14} className="text-slate-400" /> {t(lang, 'apiMediaVideo')}
                                </h4>
                                <p className="text-sm text-slate-600 leading-relaxed font-medium">
                                    {t(lang, 'apiInfoDesc3')}
                                </p>
                            </section>

                            <section>
                                <h4 className="text-slate-900 font-black uppercase tracking-widest text-[11px] mb-3 flex items-center gap-2">
                                    <Newspaper size={14} className="text-slate-400" /> {t(lang, 'apiNewsAssets')}
                                </h4>
                                <div className="space-y-4">
                                    <p className="text-sm text-slate-600 leading-relaxed font-medium">
                                        {t(lang, 'apiInfoDesc4')}
                                    </p>
                                    <a
                                        href="https://www.bbc.co.uk/news/10628494"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-[13px] font-bold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-all active:scale-95 group"
                                    >
                                        {t(lang, 'apiBbcSource')}
                                        <ExternalLink size={14} className="text-slate-400 group-hover:text-slate-600 transition-colors" />
                                    </a>
                                </div>
                            </section>
                        </div>

                        <div className="p-4 bg-rose-50 rounded-[24px] border border-rose-100">
                            <h3 className="text-lg font-black text-slate-900 mb-6 flex items-center gap-3">
                                <Copyright size={16} className="text-rose-600" /> {t(lang, 'apiCopyrightDisclaimer')}
                            </h3>
                            <div className="space-y-4 text-sm text-slate-700 leading-relaxed font-medium">
                                <p>
                                    {t(lang, 'apiInfoDesc5')}
                                </p>
                                <p>
                                    {t(lang, 'apiInfoDesc6')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AppFooter lang={lang} />
        </div>
    );
};
